// Composer Model + Effort selector — Sprint UI-10 (shadcn Popover).
//
// Migrated from hand-rolled click-outside + manual popover to shadcn
// Popover primitive. Preserves the 2-column layout (model left, effort
// right) and full keyboard navigation.
//
// DS.6 (sprint aos-ds-composer, §2 D3/D4 branch B, §10 Q6) — shadcn `Popover`
// + ghost `Button` are gone; the trigger is a real `@ds/data-display/Chip
// variant="filter"` <button> handed its props by `ds/DsChipPopover`.
//
// THE GRID AND ITS HANDLER ARE KEPT VERBATIM, and that is the decision, not an
// omission: this component already implements a full 2-D roving model —
// ArrowUp/Down walk the MODEL column, ArrowLeft/Right the EFFORT column of the
// FOCUSED model (both wrapping, model-dependent), Enter commits both axes —
// over `role="menuitemradio"` rows with `aria-checked`. `@ds/overlays/Menu`'s
// 1-D `items[]` cannot represent two independent wrapping axes and would
// flatten `menuitemradio` into `menuitem`: a strict keyboard and a11y
// REGRESSION, not a gain (spec §0.20). Only the trigger and the panel chrome
// change. Escape is the one behaviour the swap had to restore by hand — the DS
// `Popover`'s listener is document-level and returns focus to nothing, so
// `DsChipPopover` closes AND refocuses the trigger.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  effortsForModel,
  MODEL_OPTIONS,
  reconcileEffort,
  EFFORT_OPTIONS,
  modelSupportsEffort,
} from '../lib/models';
import { useTranslation } from '../i18n';
import type { EffortLevel } from '../types';
import Chip from '@ds/data-display/Chip';
import { Icon } from './atoms/Icon';
import { DsChipPopover } from './ds/DsChipPopover';

export interface ModelEffortChipsProps {
  model: string;
  effort: EffortLevel;
  onModelChange: (model: string) => void;
  onEffortChange: (effort: EffortLevel) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

function shortModelLabel(value: string): string {
  // Canonical-ID lookup only — see sprint fix-opus-alias-drift (2026-06-03).
  // Falling through to the raw value for unknown strings is deliberate:
  // it surfaces stored CLI aliases ('opus', 'sonnet', etc.) as the
  // literal alias instead of silently mapping them to a chip label that
  // disagrees with what the runner CLI actually invokes.
  const opt = MODEL_OPTIONS.find((m) => m.value === value);
  return opt ? opt.label : value;
}

function shortEffortLabel(effort: EffortLevel): string {
  const opt = EFFORT_OPTIONS.find((e) => e.value === effort);
  return opt ? opt.label : effort;
}

export function ModelEffortChips({
  model,
  effort,
  onModelChange,
  onEffortChange,
  disabled,
  size = 'sm',
}: ModelEffortChipsProps) {
  const { t } = useTranslation();
  const fontClass = size === 'md' ? 'text-body' : 'text-chip';
  const [open, setOpen] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  const [focused, setFocused] = useState<{ model: string; effort: EffortLevel }>(
    () => ({ model, effort }),
  );

  useEffect(() => {
    if (!open) return;
    setFocused({ model, effort });
  }, [open, model, effort]);

  const modelLabel = useMemo(() => shortModelLabel(model), [model]);
  const effortLabel = useMemo(() => shortEffortLabel(effort), [effort]);
  const hasEffort = useMemo(() => modelSupportsEffort(model), [model]);

  const focusedEffortOptions = useMemo(
    () => effortsForModel(focused.model),
    [focused.model],
  );
  const focusedHasEffort = focusedEffortOptions.length > 0;

  function commit(nextModel: string, nextEffort: EffortLevel) {
    if (nextModel !== model) onModelChange(nextModel);
    const reconciled = reconcileEffort(nextModel, nextEffort);
    if (reconciled !== effort || nextModel !== model) {
      onEffortChange(reconciled);
    }
    setOpen(false);
  }

  function moveModel(delta: 1 | -1) {
    const idx = MODEL_OPTIONS.findIndex((m) => m.value === focused.model);
    const nextIdx = (idx + delta + MODEL_OPTIONS.length) % MODEL_OPTIONS.length;
    const nextModel = MODEL_OPTIONS[nextIdx]!.value;
    const nextEffort = reconcileEffort(nextModel, focused.effort);
    setFocused({ model: nextModel, effort: nextEffort });
  }

  function moveEffort(delta: 1 | -1) {
    const opts = effortsForModel(focused.model);
    const idx = opts.findIndex((e) => e.value === focused.effort);
    const nextIdx = (idx + delta + opts.length) % opts.length;
    setFocused((prev) => ({ ...prev, effort: opts[nextIdx]!.value }));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveModel(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveModel(-1);
      return;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      moveEffort(1);
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      moveEffort(-1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(focused.model, focused.effort);
    }
  }

  return (
    <div
      className={`relative inline-flex items-center ${fontClass}`}
      data-testid="model-effort-chips"
    >
      <DsChipPopover
        open={open}
        onOpenChange={setOpen}
        disabled={disabled}
        data-testid="model-effort-chip-button"
        hasPopup="dialog"
        panelStyle={{ minWidth: 'auto' }}
        onOpenAutoFocus={() => gridRef.current?.focus()}
        renderTrigger={(trigger) => (
          <Chip
            {...trigger}
            variant="filter"
            size={size === 'md' ? 'sm' : 'xs'}
            // `Chip.label` is a string, so the `model · effort` composition is
            // one interpolated label — the shape the approved mockup uses.
            label={hasEffort ? `${modelLabel} · ${effortLabel}` : modelLabel}
            trailing={<Icon name="ChevronDown" size={10} aria-hidden />}
            aria-label={t('modelChip.modelAndEffortAriaLabel', {
              model: modelLabel,
              effort: effortLabel,
            })}
          />
        )}
      >
          <div
            ref={gridRef}
            role="menu"
            aria-label={t('modelChip.popoverAriaLabel')}
            tabIndex={-1}
            onKeyDown={onKeyDown}
            data-testid="model-effort-popover"
            className="flex flex-row focus:outline-none"
          >
            {/* LEFT — model column */}
            <div className={`flex flex-col py-1 min-w-[10rem] ${focusedHasEffort ? 'border-r border-border' : ''}`}>
              {MODEL_OPTIONS.map((opt) => {
                const isFocused = opt.value === focused.model;
                const isSelected = opt.value === model;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isSelected}
                    onMouseEnter={() => setFocused((prev) => ({ ...prev, model: opt.value }))}
                    onClick={() => commit(opt.value, focused.effort)}
                    data-testid={`model-option-${opt.value}`}
                    className={`flex items-center gap-2 px-3 py-1.5 text-left text-sm ${isFocused ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'}`}
                    style={{ minHeight: 'auto', minWidth: 'auto' }}
                  >
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSelected && (
                      <Icon name="Check" size={14} className="shrink-0 text-primary" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>

            {/* RIGHT — effort column (only when the focused model supports effort) */}
            {focusedHasEffort && (
              <div className="flex flex-col py-1 min-w-[8rem]">
                {focusedEffortOptions.map((opt) => {
                  const isFocused = opt.value === focused.effort;
                  const isSelected = opt.value === effort && model === focused.model;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isSelected}
                      onMouseEnter={() => setFocused((prev) => ({ ...prev, effort: opt.value }))}
                      onClick={() => commit(focused.model, opt.value)}
                      data-testid={`effort-option-${opt.value}`}
                      className={`flex items-center gap-2 px-3 py-1.5 text-left text-sm ${isFocused ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'}`}
                      style={{ minHeight: 'auto', minWidth: 'auto' }}
                    >
                      <span className="flex-1">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
      </DsChipPopover>
    </div>
  );
}
